
import type { BreakerSelectivityRunCreateRequest } from "../services/breakerSelectivity";
import { downloadRunJson } from "../services/runExport";
import { BreakerSelectivityForm } from "../components/BreakerSelectivityForm";
import { BreakerSelectivityResultPanel } from "../components/BreakerSelectivityResultPanel";
import { BreakerSelectivityResultSummary } from "../components/BreakerSelectivityResultSummary";
import { BreakerSelectivityWarningPanel } from "../components/BreakerSelectivityWarningPanel";
import { ProtectionSwitch } from "../components/ProtectionSwitch";
import { RunTraceabilityPanel } from "../components/RunTraceabilityPanel";
import { SldLinkPanel } from "../components/SldLinkPanel";
import { StudyProjectLine } from "../components/StudyProjectLine";
import { useBreakerSelectivity } from "../hooks/useBreakerSelectivity";
import { useSldElements } from "../hooks/useSldElements";

function describeError(error: unknown): string {
  if (error instanceof Error && error.message.trim().length > 0) {
    return error.message;
  }
  return "The breaker duty and selectivity study could not be completed.";
}

// Study-page layout (Master Prompt v2.1 section 19): collapsible inputs beside
// a result-first column - summary, warnings, traceability, then detail tables.
// EOS-05 is delivered in parts (A20, A21); this page is 05b, reached through the
// protection switch beside the starter coordination page.
export function BreakerSelectivityPage() {
  const { calculate, reset, result, run, error, isPending, isError } = useBreakerSelectivity();
  // The active revision's single-line diagram, for the bus and device pickers (A27 (b)).
  const { elements: sldElements } = useSldElements();

  // Errors are surfaced through the hook state below, not thrown into the form.
  async function handleSubmit(payload: BreakerSelectivityRunCreateRequest): Promise<void> {
    try {
      await calculate(payload);
    } catch {
      // Intentionally empty: isError / error already describe the failure.
    }
  }

  return (
    <main data-breaker-selectivity-page>
      <ProtectionSwitch />
      <header>
        <p>EOS-05 · Protection coordination</p>
        <h1>Breaker duty and selectivity</h1>
        <p>
          Check each breaker's declared breaking, making and service capacity and the declared
          selectivity with its incomer against the fault level and peak current of a stored
          short-circuit study of the same project revision, using the validated KES Electrical
          OS calculation service. Duty and selectivity are proven only by the manufacturer's
          tested data.
        </p>
      </header>

      <div data-study-layout>
        <section aria-labelledby="breaker-selectivity-inputs-heading">
          <details open data-study-inputs>
            <summary>
              <h2 id="breaker-selectivity-inputs-heading">Inputs</h2>
            </summary>
            <StudyProjectLine />
            <BreakerSelectivityForm
              disabled={isPending}
              onSubmit={handleSubmit}
              sldElements={sldElements}
            />
          </details>
        </section>

        <section aria-labelledby="breaker-selectivity-results-heading" data-study-results>
          <h2 id="breaker-selectivity-results-heading">Results</h2>

          {isPending ? (
            <p role="status" data-calculation-state="pending">
              Checking the breaker duty and selectivity…
            </p>
          ) : null}

          {isError ? (
            <p role="alert" data-calculation-state="error">
              {describeError(error)}
            </p>
          ) : null}

          {result ? (
            <div data-calculation-state="success">
              <BreakerSelectivityResultSummary result={result} />
              {run ? <SldLinkPanel link={run.sld_link} /> : null}
              <BreakerSelectivityWarningPanel result={result} />
              {run ? (
                <RunTraceabilityPanel
                  run={run}
                  onExport={() =>
                    downloadRunJson(
                      { run, result },
                      `${run.calculation_key}-rev${run.revision_number}.json`,
                    )
                  }
                />
              ) : null}
              <BreakerSelectivityResultPanel result={result} />
              <p>
                <button type="button" onClick={reset}>
                  Clear results
                </button>
              </p>
            </div>
          ) : null}

          {!isPending && !isError && !result ? (
            <p data-calculation-state="idle">
              Submit the inputs to check the breaker duty and selectivity.
            </p>
          ) : null}
        </section>
      </div>

      <section aria-labelledby="breaker-selectivity-review-heading">
        <h2 id="breaker-selectivity-review-heading">Engineering review required</h2>
        <p>
          Calculation results are engineering evidence, not an automatic compliance
          declaration. Confirm the linked fault study, each breaker's ratings and duty basis,
          and every declared selectivity or cascading value against the manufacturer's tables
          for the exact device pair before approval or issue.
        </p>
      </section>
    </main>
  );
}

export default BreakerSelectivityPage;
